// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { datadogRum } from '@datadog/browser-rum';

/**
 * Error types for categorization in Datadog
 */
export type ErrorType = 'http' | 'validation' | 'js' | 'custom';

/**
 * Feature names for error tracking context
 */
export type FeatureName =
  | 'key-projects'
  | 'memberships'
  | 'events'
  | 'training'
  | 'employees'
  | 'code-contributions'
  | 'home'
  | 'auth'
  | 'settings'
  | 'search'
  | 'unknown';

/**
 * Context object for error tracking
 * Provides structured metadata for Datadog error analysis
 */
export interface ErrorContext {
  /** Feature area where the error occurred */
  feature?: FeatureName;
  /** Specific user action that triggered the error */
  action?: string;
  /** Workspace ID if applicable */
  workspaceId?: string;
  /** Project slug if applicable */
  projectSlug?: string;
  /** HTTP status code for HTTP errors */
  httpStatus?: number;
  /** HTTP request URL (sanitized) */
  httpUrl?: string;
  /** HTTP method (GET, POST, etc.) */
  httpMethod?: string;
  /** Request duration in milliseconds */
  httpDuration?: number;
  /** Validation errors for form failures */
  validationErrors?: string[];
  /** Component name where error occurred */
  component?: string;
  /** Additional custom context */
  [key: string]: unknown;
}

/**
 * Structured error data sent to Datadog
 */
interface DatadogErrorData {
  type: ErrorType;
  feature: FeatureName;
  action?: string;
  http?: {
    status?: number;
    url?: string;
    method?: string;
    duration?: number;
    statusText?: string;
  };
  validation?: {
    fields: string[];
    count: number;
  };
  context?: Record<string, unknown>;
  timestamp: string;
}

/**
 * Centralized service for tracking errors to Datadog RUM
 *
 * This service provides a consistent interface for tracking different types of errors:
 * - HTTP errors (4xx, 5xx responses)
 * - Form validation errors
 * - JavaScript runtime errors
 * - Custom business logic errors
 *
 * All errors are enriched with context information for better debugging and analysis.
 *
 * @example
 * ```typescript
 * // Track an HTTP error
 * this.errorTracking.trackHttpError(httpError, {
 *   feature: 'key-projects',
 *   action: 'create-workspace',
 * });
 *
 * // Track a validation error
 * this.errorTracking.trackValidationError({
 *   feature: 'key-projects',
 *   action: 'create-workspace',
 *   validationErrors: ['workspaceName'],
 * });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class DatadogErrorTrackingService {
  /**
   * Check if Datadog RUM is initialized
   */
  private isDatadogInitialized(): boolean {
    return !!datadogRum.getInitConfiguration();
  }

  /**
   * Track an HTTP error (4xx, 5xx responses)
   *
   * @param error - The HttpErrorResponse from Angular's HttpClient
   * @param context - Additional context for the error
   */
  trackHttpError(error: HttpErrorResponse, context: ErrorContext = {}): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    const errorData: DatadogErrorData = {
      type: 'http',
      feature: context.feature || 'unknown',
      action: context.action,
      http: {
        status: error.status,
        url: this.sanitizeUrl(context.httpUrl || error.url || ''),
        method: context.httpMethod,
        duration: context.httpDuration,
        statusText: error.statusText,
      },
      context: this.buildContext(context),
      timestamp: new Date().toISOString(),
    };

    const errorMessage = this.buildHttpErrorMessage(error, context);

    datadogRum.addError(new Error(errorMessage), {
      error: errorData,
    });
  }

  /**
   * Track a form validation error
   *
   * @param context - Context including validation error details
   */
  trackValidationError(context: ErrorContext): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    const validationErrors = context.validationErrors || [];

    const errorData: DatadogErrorData = {
      type: 'validation',
      feature: context.feature || 'unknown',
      action: context.action,
      validation: {
        fields: validationErrors,
        count: validationErrors.length,
      },
      context: this.buildContext(context),
      timestamp: new Date().toISOString(),
    };

    const errorMessage = `Validation failed: ${validationErrors.join(', ')} [${context.feature}/${context.action}]`;

    datadogRum.addError(new Error(errorMessage), {
      error: errorData,
    });
  }

  /**
   * Track a JavaScript runtime error
   *
   * @param error - The JavaScript Error object
   * @param context - Additional context for the error
   */
  trackJsError(error: Error, context: ErrorContext = {}): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    const errorData: DatadogErrorData = {
      type: 'js',
      feature: context.feature || 'unknown',
      action: context.action,
      context: {
        ...this.buildContext(context),
        errorName: error.name,
        stack: error.stack?.substring(0, 1000), // Limit stack trace size
      },
      timestamp: new Date().toISOString(),
    };

    datadogRum.addError(error, {
      error: errorData,
    });
  }

  /**
   * Track a custom business logic error
   *
   * @param message - Error message describing the issue
   * @param context - Additional context for the error
   */
  trackCustomError(message: string, context: ErrorContext = {}): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    const errorData: DatadogErrorData = {
      type: 'custom',
      feature: context.feature || 'unknown',
      action: context.action,
      context: this.buildContext(context),
      timestamp: new Date().toISOString(),
    };

    const errorMessage = `${message} [${context.feature}/${context.action || 'unknown'}]`;

    datadogRum.addError(new Error(errorMessage), {
      error: errorData,
    });
  }

  /**
   * Track a user action (for correlation with errors)
   *
   * @param actionName - Name of the action
   * @param context - Additional context
   */
  trackAction(actionName: string, context: ErrorContext = {}): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    datadogRum.addAction(actionName, {
      feature: context.feature,
      action: context.action,
      ...this.buildContext(context),
    });
  }

  /**
   * Build a human-readable error message for HTTP errors
   */
  private buildHttpErrorMessage(error: HttpErrorResponse, context: ErrorContext): string {
    const feature = context.feature || 'unknown';
    const action = context.action || 'request';
    const status = error.status;
    const method = context.httpMethod || 'HTTP';

    return `${method} ${status} - ${action} failed [${feature}]`;
  }

  /**
   * Sanitize URL by removing sensitive information
   * Replaces UUIDs, IDs, and sensitive params with placeholders
   */
  private sanitizeUrl(url: string): string {
    if (!url) return '';

    return url
      // Remove query parameters that might contain sensitive data
      .replace(/\?.*$/, '')
      // Replace UUIDs
      .replace(
        /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
        '{uuid}'
      )
      // Replace numeric IDs in paths
      .replace(/\/\d{10,}/g, '/{id}')
      // Replace Salesforce-style IDs (alphanumeric, typically 15-18 chars)
      .replace(/\/[a-zA-Z0-9]{15,18}(?=\/|$)/g, '/{sfId}');
  }

  /**
   * Build context object, filtering out internal properties
   */
  private buildContext(context: ErrorContext): Record<string, unknown> {
    const { feature, action, httpStatus, httpUrl, httpMethod, httpDuration, validationErrors, ...rest } = context;

    // Filter out undefined values and limit object size
    const filteredContext: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(rest)) {
      if (value !== undefined && value !== null) {
        // Limit string values
        if (typeof value === 'string' && value.length > 500) {
          filteredContext[key] = value.substring(0, 500) + '...';
        } else {
          filteredContext[key] = value;
        }
      }
    }

    return filteredContext;
  }

  /**
   * Infer feature name from URL path
   *
   * @param url - The request URL
   * @returns The inferred feature name
   */
  inferFeatureFromUrl(url: string): FeatureName {
    if (!url) return 'unknown';

    const urlLower = url.toLowerCase();

    if (urlLower.includes('/workspaces') || urlLower.includes('/key-projects')) {
      return 'key-projects';
    }
    if (urlLower.includes('/memberships')) {
      return 'memberships';
    }
    if (urlLower.includes('/events')) {
      return 'events';
    }
    if (urlLower.includes('/training') || urlLower.includes('/certifications')) {
      return 'training';
    }
    if (urlLower.includes('/employees')) {
      return 'employees';
    }
    if (urlLower.includes('/contributions') || urlLower.includes('/code-')) {
      return 'code-contributions';
    }
    if (urlLower.includes('/auth') || urlLower.includes('/login')) {
      return 'auth';
    }

    return 'unknown';
  }
}

